import {
  atualizarPrestadorSchema,
  criarPrestadorSchema,
  ROTULO_DO_STATUS_DE_PRESTADOR,
  ROTULO_DO_TIPO_DE_PRESTADOR,
  STATUS_DE_PRESTADOR,
  type StatusDoPrestador,
  TIPOS_DE_PRESTADOR,
  type TipoDePrestador,
} from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useLocaisAtivos } from '@/features/frota/api'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { apenasDigitos, mascararCpfProgressivo, mascararTelefoneProgressivo } from '@/lib/mascaras'
import { usePermissao } from '@/lib/permissoes'

import { type Prestador, useAtualizarPrestador, useCriarPrestador } from './cotacao'

/**
 * Cadastro de motorista e de segurança.
 *
 * ---------------------------------------------------------------------------
 * `custoDiario` É O QUE A OPERAÇÃO PAGA, NUNCA O QUE O CLIENTE PAGA
 * ---------------------------------------------------------------------------
 * O preço ao cliente é do `Extra` (`MOTORISTA`, `SEGURANCA`), e vive na tela de
 * extras. Aqui entra o custo — e é a distância entre os dois que vira margem no
 * relatório de rentabilidade da Fase 7. Misturar os dois campos apagaria a única
 * pergunta que este cadastro existe para responder: o serviço se paga?
 *
 * ---------------------------------------------------------------------------
 * BASE NULA É "ATENDE DE QUALQUER LUGAR", NÃO "FALTOU PREENCHER"
 * ---------------------------------------------------------------------------
 * A consulta de prestadores livres conta quem não tem base em TODA base. Por
 * isso a opção vazia do seletor é escrita por extenso: um traço ali seria lido
 * como cadastro incompleto, e alguém "consertaria" prendendo o coringa a uma
 * unidade — reduzindo a disponibilidade de todas as outras sem perceber.
 *
 * ---------------------------------------------------------------------------
 * INATIVAR NÃO É APAGAR, E NÃO SE FAZ DAQUI
 * ---------------------------------------------------------------------------
 * A escala passada aponta para o prestador e o relatório soma o custo dele em
 * locações já faturadas. O `status` é editável neste formulário justamente
 * porque é por ele que alguém VOLTA para a escala; tirar de circulação é ação de
 * linha na listagem, com o aviso de que os turnos futuros continuam de pé.
 */

const CAMPOS = [
  'nome',
  'tipo',
  'telefone',
  'email',
  'cpf',
  'custoDiario',
  'locationId',
  'status',
  'observacoes',
] as const

const TIPO_PADRAO: TipoDePrestador = TIPOS_DE_PRESTADOR[0]

interface FormularioDePrestador {
  nome: string
  tipo: TipoDePrestador
  telefone: string
  email: string
  cpf: string
  /** Reais digitados (`"350,00"`), convertidos para centavos no envio. */
  custoDiario: string
  locationId: string
  status: StatusDoPrestador
  observacoes: string
}

function valoresIniciais(prestador: Prestador | undefined): FormularioDePrestador {
  return {
    nome: prestador?.nome ?? '',
    tipo: prestador?.tipo ?? TIPO_PADRAO,
    telefone: mascararTelefoneProgressivo(prestador?.telefone ?? ''),
    email: prestador?.email ?? '',
    cpf: mascararCpfProgressivo(prestador?.cpf ?? ''),
    custoDiario: paraCampoDeReais(prestador?.custoDiario ?? undefined),
    locationId: prestador?.locationId ?? '',
    status: prestador?.status ?? 'ATIVO',
    observacoes: prestador?.observacoes ?? '',
  }
}

export interface DialogoDePrestadorProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cadastra; preenchido edita. */
  readonly prestador?: Prestador | undefined
}

export function DialogoDePrestador({
  aberto,
  aoFechar,
  prestador,
}: DialogoDePrestadorProps): ReactElement {
  const editando = prestador !== undefined
  const podeVerCusto = usePermissao('service_provider.cost')

  /**
   * ⚠️ O CAMPO DE CUSTO SOME NA EDIÇÃO DE QUEM NÃO PODE LÊ-LO — e isto não é
   * cosmético, é o conserto de um jeito muito fácil de destruir dado.
   *
   * A API passou a devolver `custoDiario: null` para quem não tem
   * `service_provider.cost`. Se o formulário continuasse montando o campo, ele
   * pré-preencheria com VAZIO e o `PATCH` mandaria o campo vazio junto — o
   * OPERACIONAL abriria o cadastro para corrigir um telefone e zeraria, sem
   * ver, o que a locadora paga àquele motorista. O `null` do GET viraria um
   * `0` real no banco.
   *
   * Na CRIAÇÃO o campo continua lá e obrigatório: informar um valor que não se
   * pode reler depois é a mesma assimetria de trocar uma senha. É diferente de
   * apagar o valor de outra pessoa sem saber que apagou.
   */
  const editandoSemCusto = editando && !podeVerCusto
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const locais = useLocaisAtivos()
  const criar = useCriarPrestador()
  const atualizar = useAtualizarPrestador()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDePrestador>({ defaultValues: valoresIniciais(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir "editar"
  // depois de "novo" mostraria os valores do formulário anterior.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(prestador))
    }
  }, [aberto, prestador, reset])

  const telefoneDigitado = watch('telefone')
  const cpfDigitado = watch('cpf')
  const ocupado = criar.isPending || atualizar.isPending

  const listaDeLocais = locais.data?.itens ?? []

  /**
   * `useLocaisAtivos` só traz base ATIVA. Quando a base do prestador foi
   * desativada depois do cadastro, o `<select>` não teria opção com aquele
   * valor — o navegador cai para a opção vazia e salvar mandaria
   * `locationId: null`, APAGANDO a base sem ninguém pedir. A opção abaixo
   * mantém o valor selecionável (e nomeado) até que alguém decida trocá-lo.
   */
  const baseDoPrestador = prestador?.locationId ?? ''
  const baseForaDaLista =
    baseDoPrestador.length > 0 &&
    !locais.isPending &&
    !listaDeLocais.some((local) => local.id === baseDoPrestador)

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    // `null` aqui tem DOIS sentidos, e eles não podem se confundir: quando o
    // campo está em jogo, `null` é "digitou coisa inválida"; quando ele nem foi
    // montado, o custo simplesmente não faz parte deste envio.
    const custoWire = editandoSemCusto ? null : deCampoDeReaisParaWire(valores.custoDiario)

    if (!editandoSemCusto && custoWire === null) {
      setError('custoDiario', {
        type: 'validate',
        message: 'valor inválido — use o formato 350,00',
      })
      return
    }

    const telefone = apenasDigitos(valores.telefone)
    const cpf = apenasDigitos(valores.cpf)
    const email = valores.email.trim()
    const observacoes = valores.observacoes.trim()

    if (editando) {
      // Campo vazio vira `null` (e não ausência): `undefined` significa "não
      // mexa", e apagar o telefone de quem saiu da operação precisa APAGAR.
      const entrada = {
        nome: valores.nome,
        tipo: valores.tipo,
        telefone: telefone.length > 0 ? telefone : null,
        email: email.length > 0 ? email : null,
        cpf: cpf.length > 0 ? cpf : null,
        // Ausente = "não mexa". É a diferença entre não poder ver e apagar.
        ...(custoWire === null ? {} : { custoDiario: custoWire }),
        locationId: valores.locationId.length > 0 ? valores.locationId : null,
        status: valores.status,
        observacoes: observacoes.length > 0 ? observacoes : null,
      }

      const validado = atualizarPrestadorSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `entrada`, e não `validado.data`: o schema transforma `custoDiario` em
      // `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
      atualizar.mutate(
        { id: prestador.id, dados: entrada },
        {
          onSuccess: (salvo) => {
            toast.success(`${salvo.nome} atualizado`, {
              description:
                salvo.status === 'ATIVO'
                  ? 'Ele volta a contar como disponível na escala.'
                  : 'Ele sai da contagem de prestadores livres.',
            })
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    const entrada = {
      nome: valores.nome,
      tipo: valores.tipo,
      custoDiario: custoWire,
      status: valores.status,
      ...(telefone.length > 0 ? { telefone } : {}),
      ...(email.length > 0 ? { email } : {}),
      ...(cpf.length > 0 ? { cpf } : {}),
      ...(valores.locationId.length > 0 ? { locationId: valores.locationId } : {}),
      ...(observacoes.length > 0 ? { observacoes } : {}),
    }

    const validado = criarPrestadorSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    criar.mutate(entrada, {
      onSuccess: (criado) => {
        toast.success(`${criado.nome} cadastrado`, {
          description: `${ROTULO_DO_TIPO_DE_PRESTADOR[criado.tipo]} disponível para a escala.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar prestador' : 'Novo prestador'}</DialogTitle>
          <DialogDescription>
            Motorista e segurança são serviços vendidos como extra — e só entram na cotação quando
            há alguém livre no período.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Nome" obrigatorio erro={errors.nome?.message} className="sm:col-span-2">
              {(campo) => (
                <Input
                  {...campo}
                  {...register('nome', { required: 'informe o nome' })}
                  autoComplete="name"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Tipo" obrigatorio erro={errors.tipo?.message}>
              {(campo) => (
                <Select {...campo} {...register('tipo')} disabled={ocupado}>
                  {TIPOS_DE_PRESTADOR.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {ROTULO_DO_TIPO_DE_PRESTADOR[tipo]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            {editandoSemCusto ? null : (
              <Campo
                rotulo="Custo por diária"
                obrigatorio
                erro={errors.custoDiario?.message}
                dica="O que a operação PAGA por dia de serviço. O preço ao cliente é do extra."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('custoDiario', {
                      required: 'informe o custo diário',
                      validate: (valor: string) =>
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 350,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            )}

            <Campo rotulo="Telefone" erro={errors.telefone?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('telefone')}
                  value={telefoneDigitado}
                  onChange={(evento) => {
                    // `shouldValidate` só depois do primeiro envio: validar a
                    // cada tecla acusaria telefone inválido no terceiro dígito.
                    setValue('telefone', mascararTelefoneProgressivo(evento.target.value), {
                      shouldValidate: isSubmitted,
                    })
                  }}
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(11) 99999-9999"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="E-mail" erro={errors.email?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="CPF"
              erro={errors.cpf?.message}
              dica="Identificação interna na escala — opcional."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('cpf')}
                  value={cpfDigitado}
                  onChange={(evento) => {
                    setValue('cpf', mascararCpfProgressivo(evento.target.value), {
                      shouldValidate: isSubmitted,
                    })
                  }}
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Base"
              erro={errors.locationId?.message}
              dica="Sem base, atende a partir de qualquer ponto — e conta como livre em todos."
            >
              {(campo) => (
                <Select
                  {...campo}
                  {...register('locationId')}
                  disabled={ocupado || locais.isPending}
                >
                  <option value="">
                    {locais.isPending ? 'Carregando…' : 'Atende de qualquer base'}
                  </option>
                  {baseForaDaLista ? (
                    <option value={baseDoPrestador}>Base atual (desativada)</option>
                  ) : null}
                  {listaDeLocais.map((local) => (
                    <option key={local.id} value={local.id}>
                      {local.nome} · {local.cidade}/{local.uf}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Situação"
              obrigatorio
              erro={errors.status?.message}
              dica="Inativo some da escala e da contagem de prestadores livres."
            >
              {(campo) => (
                <Select {...campo} {...register('status')} disabled={ocupado}>
                  {STATUS_DE_PRESTADOR.map((status) => (
                    <option key={status} value={status}>
                      {ROTULO_DO_STATUS_DE_PRESTADOR[status]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Observações"
              erro={errors.observacoes?.message}
              className="sm:col-span-2"
              dica="Restrição de horário, idioma, treinamento de direção defensiva."
            >
              {(campo) => (
                <Textarea {...campo} {...register('observacoes')} rows={3} disabled={ocupado} />
              )}
            </Campo>
          </div>

          {locais.isError ? (
            <Alert variant="warning">
              <AlertTitle>Não foi possível carregar as bases</AlertTitle>
              <AlertDescription>
                A lista não veio, então o seletor de base fica só com &quot;atende de qualquer
                base&quot;. Dá para salvar assim — mas, se este prestador já tinha base, salve
                depois: gravar agora trocaria a base dele por &quot;qualquer&quot;.
              </AlertDescription>
            </Alert>
          ) : locais.isPending ? null : listaDeLocais.length === 0 ? (
            <Alert variant="info">
              <AlertTitle>Nenhuma base ativa cadastrada</AlertTitle>
              <AlertDescription>
                Sem ponto de retirada ativo, todo prestador atende a partir de qualquer base — e
                conta como livre em todas.
              </AlertDescription>
            </Alert>
          ) : null}

          {baseForaDaLista ? (
            <Alert variant="warning">
              <AlertTitle>A base deste prestador está desativada</AlertTitle>
              <AlertDescription>
                Ela continua selecionada para que salvar não a apague sem querer. Escolha outra base
                (ou &quot;atende de qualquer base&quot;) se a mudança for intencional.
              </AlertDescription>
            </Alert>
          ) : null}

          {editando && prestador.bloqueios > 0 ? (
            <Alert variant="info">
              <AlertTitle>
                {prestador.bloqueios} {prestador.bloqueios === 1 ? 'turno' : 'turnos'} na escala
              </AlertTitle>
              <AlertDescription>
                Mudar o cadastro não mexe nos turnos já marcados. Cancelar compromisso assumido é
                decisão de cada turno, na escala.
              </AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Cadastrar prestador'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
